import React, { useState, useEffect } from 'react';
import StepLayout from './StepLayout';

// B-SELLER-RETRY (2026-06-02): a genuine in-task reconnect usually heals on
// its own within a few seconds, so the recovering screen hides the manual
// "Try again" CTA for this long before offering it. Only shown when the
// envelope's can_retry is true (the BE has parked the session in a
// seller-gateable state).
export const SELLER_RETRY_CTA_DELAY_MS = 45000;

// B-RELEASE-ON-FAILURE (2026-06-13): subtle "Start over" CTA shared by every
// release-class screen below. App passes `onStartOver` (a hard storage clear +
// reload into a fresh Step-1 wizard) ONLY when the kill switch is ON and the
// session disposition is 'release', so when the flag is OFF — or on a pinned
// live-challenge surface — this renders nothing and the screen is byte-identical
// to before. Rendered as a low-emphasis ghost button so it never competes with a
// screen's primary CTA (Try again / Restart secure step).
export function StartOverButton({ onStartOver, label = 'Start over' }) {
  if (!onStartOver) return null;
  return (
    <button
      type="button"
      onClick={onStartOver}
      style={{
        marginTop: 4,
        background: 'transparent',
        color: 'var(--text2, #555)',
        border: '1px solid var(--border, #d1d5db)',
        borderRadius: 8,
        padding: '10px 20px',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

// PR 4 — status screens that map to non-input BE states. The state
// envelope's `status` drives which screen App.jsx renders; each is a
// passive surface (no submit, no input — they just describe what's
// happening so the seller doesn't think the wizard hung).
//
// All screens accept a `message` override so the BE's
// `status_message_safe_for_seller` field can replace the default copy
// without a FE deploy.

export function RecoveringScreen({
  message,
  recoveryAttemptCount,
  canRetry = false,
  onRetry,
  retrying = false,
  onStartOver,
  // RESET-CODE-INBOX-NUDGE (2026-10-09, thread reset-code-wizard-nudge):
  // App passes this true ONLY for the password-reset recovering variant AND
  // ONLY when RESET_CODE_INBOX_NUDGE_ENABLED is on. When true, the passive
  // "you don't need to do anything" secondary line — actively wrong for a
  // reset, where the seller must fetch an emailed code moments from now — is
  // replaced with an active "watch your inbox" nudge so the seller is primed
  // to enter the code the instant the entry prompt surfaces. Default false
  // keeps every other recovering surface byte-identical.
  resetInboxNudge = false,
}) {
  // "Still working on your setup…" — chosen by directive. NOT a failure
  // message: a recovering session can still complete. The attempt
  // counter is surfaced so a multi-attempt recovery shows progress.
  //
  // B-SELLER-RETRY (2026-06-02): when the envelope marks the session as
  // seller-retryable (can_retry), surface an optional "Try again" CTA —
  // but only AFTER SELLER_RETRY_CTA_DELAY_MS so a genuine in-task
  // reconnect heals silently first. The timer is reset whenever the gate
  // flips off so a later recovering pass starts the delay fresh.
  const [retryCtaVisible, setRetryCtaVisible] = useState(false);
  useEffect(() => {
    if (!canRetry || !onRetry) {
      setRetryCtaVisible(false);
      return undefined;
    }
    const t = setTimeout(() => setRetryCtaVisible(true), SELLER_RETRY_CTA_DELAY_MS);
    return () => clearTimeout(t);
  }, [canRetry, onRetry]);

  return (
    <StepLayout title="Still working on your setup" variant="centered">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '32px 0',
      }}>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          border: '4px solid rgba(37, 99, 235, 0.15)',
          borderTopColor: 'var(--primary, #2563eb)',
          animation: 'spin 1.2s linear infinite',
        }} />
        <p style={{
          color: 'var(--text)',
          fontSize: 16,
          fontWeight: 600,
          textAlign: 'center',
          margin: 0,
        }}>
          {message || "We're sorting out a hiccup. Give us a moment."}
        </p>
        {resetInboxNudge ? (
          <div style={{
            background: 'var(--primary-soft, rgba(37, 99, 235, 0.08))',
            border: '1px solid rgba(37, 99, 235, 0.25)',
            borderRadius: 10,
            padding: '14px 18px',
            maxWidth: 380,
          }}>
            <p style={{
              color: 'var(--text)',
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.5,
              textAlign: 'center',
              margin: 0,
            }}>
              LinkedIn is emailing you a verification code.
            </p>
            <p style={{
              color: 'var(--text2)',
              fontSize: 13,
              lineHeight: 1.5,
              textAlign: 'center',
              margin: '6px 0 0',
            }}>
              Open your email now and keep this tab open — you’ll enter the code
              here in a moment.
              {recoveryAttemptCount > 1 && ` (Attempt ${recoveryAttemptCount})`}
            </p>
          </div>
        ) : (
          <p style={{
            color: 'var(--text2)',
            fontSize: 13,
            lineHeight: 1.5,
            textAlign: 'center',
            margin: 0,
            maxWidth: 360,
          }}>
            You don’t need to do anything. We’ll continue automatically.
            {recoveryAttemptCount > 1 && ` (Attempt ${recoveryAttemptCount})`}
          </p>
        )}
        {retryCtaVisible && (
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            style={{
              background: retrying ? 'var(--primary-soft)' : 'var(--primary, #2563eb)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 15,
              fontWeight: 600,
              cursor: retrying ? 'not-allowed' : 'pointer',
              opacity: retrying ? 0.75 : 1,
              marginTop: 4,
            }}
          >
            {retrying ? 'Restarting…' : 'Try again'}
          </button>
        )}
        <StartOverButton onStartOver={onStartOver} />
      </div>
    </StepLayout>
  );
}

// B-SELLER-RETRY (2026-06-02): the BE parked the session in
// status='waiting_for_seller_retry' (a blocked challenge timed out / the
// leg stalled) and set can_retry=TRUE. Unlike RecoveringScreen this is an
// explicit ask: the seller taps "Try again" and the BE flips the session
// back to in_progress and re-dispatches the blocked leg, after which the
// existing challenge projection re-surfaces the captcha / app-approval /
// code prompt. Markup/styling mirrors RecoveringScreen + EmailChangeRestartScreen.
export function SellerRetryScreen({ message, onRetry, retrying = false, onStartOver }) {
  return (
    <StepLayout title="Let’s try that again" variant="centered">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '32px 0',
      }}>
        <div style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(37, 99, 235, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          color: 'var(--primary, #2563eb)',
        }}>↻</div>
        <p style={{
          color: 'var(--text)',
          fontSize: 16,
          fontWeight: 600,
          textAlign: 'center',
          margin: 0,
        }}>
          {message
            || "That step timed out. Your details are saved. Tap below to pick up where we left off."}
        </p>
        <p style={{
          color: 'var(--text2)',
          fontSize: 13,
          lineHeight: 1.5,
          textAlign: 'center',
          margin: 0,
          maxWidth: 380,
        }}>
          We’ll resume the secure step. You may be asked to complete a
          verification again.
        </p>
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          style={{
            background: retrying ? 'var(--primary-soft)' : 'var(--primary, #2563eb)',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            padding: '12px 24px',
            fontSize: 16,
            fontWeight: 600,
            cursor: retrying ? 'not-allowed' : 'pointer',
            opacity: retrying ? 0.75 : 1,
          }}
        >
          {retrying ? 'Restarting…' : 'Try again'}
        </button>
        <StartOverButton onStartOver={onStartOver} />
      </div>
    </StepLayout>
  );
}

export function OperatorReviewScreen({ message, onStartOver }) {
  return (
    <StepLayout title="Our team is reviewing this" variant="centered">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '32px 0',
      }}>
        <div style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(245, 158, 11, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          color: '#b45309',
        }}>?</div>
        <p style={{
          color: 'var(--text)',
          fontSize: 16,
          fontWeight: 600,
          textAlign: 'center',
          margin: 0,
        }}>
          {message || "Our team is reviewing this. We'll follow up shortly."}
        </p>
        <p style={{
          color: 'var(--text2)',
          fontSize: 13,
          lineHeight: 1.5,
          textAlign: 'center',
          margin: 0,
          maxWidth: 380,
        }}>
          You can close this tab. We’ll email you the next step at the address
          you provided.
        </p>
        <StartOverButton onStartOver={onStartOver} label="Start over" />
      </div>
    </StepLayout>
  );
}

export function EmailChangeRestartScreen({
  message,
  onRestart,
  restarting = false,
  error = null,
  onStartOver,
}) {
  return (
    <StepLayout title="Restart secure browser" variant="centered">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '32px 0',
      }}>
        <div style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(37, 99, 235, 0.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          color: 'var(--primary, #2563eb)',
        }}>R</div>
        <p style={{
          color: 'var(--text)',
          fontSize: 16,
          fontWeight: 600,
          textAlign: 'center',
          margin: 0,
        }}>
          {message || 'We need to restart the secure browser step. Your details are saved.'}
        </p>
        <p style={{
          color: 'var(--text2)',
          fontSize: 13,
          lineHeight: 1.5,
          textAlign: 'center',
          margin: 0,
          maxWidth: 380,
        }}>
          This opens the secure LinkedIn step again. You may be asked for
          another LinkedIn verification code.
        </p>
        {error && (
          <p role="alert" style={{
            color: 'var(--danger, #ef4444)',
            fontSize: 13,
            lineHeight: 1.5,
            textAlign: 'center',
            margin: 0,
            maxWidth: 380,
          }}>
            {error}
          </p>
        )}
        <button
          type="button"
          onClick={onRestart}
          disabled={restarting}
          style={{
            background: restarting ? 'var(--primary-soft)' : 'var(--primary, #f97316)',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            padding: '12px 24px',
            fontSize: 16,
            fontWeight: 600,
            cursor: restarting ? 'not-allowed' : 'pointer',
            opacity: restarting ? 0.75 : 1,
          }}
        >
          {restarting ? 'Restarting...' : 'Restart secure step'}
        </button>
        <StartOverButton onStartOver={onStartOver} />
      </div>
    </StepLayout>
  );
}

export function TerminalFailedScreen({ terminalReason, message, supportEmail = 'support@sellyourlinkedin.com', onStartOver }) {
  // Real failure screen. Seller has no recovery path here — they should
  // contact support. We surface the BE's terminal_reason verbatim
  // (assumed seller-safe per the envelope contract) plus a support email.
  return (
    <StepLayout title="Something went wrong" variant="centered">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '32px 0',
      }}>
        <div style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(239, 68, 68, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          color: 'var(--danger, #ef4444)',
        }}>!</div>
        <p style={{
          color: 'var(--text)',
          fontSize: 16,
          fontWeight: 600,
          textAlign: 'center',
          margin: 0,
        }}>
          {message || terminalReason || 'We weren’t able to complete this.'}
        </p>
        <p style={{
          color: 'var(--text2)',
          fontSize: 13,
          lineHeight: 1.5,
          textAlign: 'center',
          margin: 0,
          maxWidth: 380,
        }}>
          Need help? Reach out at{' '}
          <a href={`mailto:${supportEmail}`} style={{ color: 'var(--primary)' }}>
            {supportEmail}
          </a>
          .
        </p>
        <StartOverButton onStartOver={onStartOver} label="Start over" />
      </div>
    </StepLayout>
  );
}

export function PasswordResetFailedScreen({ message, onStartOver }) {
  // B-CREDS-RECOVERY (2026-05-29): dedicated terminal screen for when the
  // automated LinkedIn password reset could not complete. The session is
  // CLOSED on the BE (status=terminal_failed, terminal_reason=
  // 'password_reset_failed') — the seller cannot retry inside it. They must
  // fix things on LinkedIn directly, then start a brand-new takeover in a
  // fresh tab (which provisions a fresh DICloak profile + buyer email).
  const stepStyle = {
    color: 'var(--text2)',
    fontSize: 14,
    lineHeight: 1.55,
    margin: 0,
  };
  return (
    <StepLayout title="We couldn’t reset your password" variant="centered">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '32px 0',
      }}>
        <div style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(239, 68, 68, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          color: 'var(--danger, #ef4444)',
        }}>!</div>
        <p style={{
          color: 'var(--text)',
          fontSize: 16,
          fontWeight: 600,
          textAlign: 'center',
          margin: 0,
        }}>
          {message
            || 'We couldn’t reset your LinkedIn password automatically.'}
        </p>
        <p style={{
          color: 'var(--text2)',
          fontSize: 13,
          lineHeight: 1.5,
          textAlign: 'center',
          margin: 0,
          maxWidth: 400,
        }}>
          Please finish this part yourself, then start again:
        </p>
        <ol style={{
          textAlign: 'left',
          maxWidth: 400,
          margin: 0,
          paddingLeft: 22,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}>
          <li style={stepStyle}>
            Open{' '}
            <a
              href="https://www.linkedin.com/login"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--primary)' }}
            >
              LinkedIn
            </a>{' '}
            directly and check your email and password are correct.
          </li>
          <li style={stepStyle}>
            If they don’t work, reset your password on LinkedIn itself
            (Sign in → “Forgot password”).
          </li>
          <li style={stepStyle}>
            Once you can log in on LinkedIn, open a{' '}
            <strong>new browser tab</strong> and start again
            from the beginning.
          </li>
        </ol>
        <StartOverButton onStartOver={onStartOver} label="Start over" />
      </div>
    </StepLayout>
  );
}

export function AccountTooYoungScreen({ message, supportEmail = 'support@sellyourlinkedin.com', onStartOver }) {
  // ACCOUNT-AGE-GATE (2026-06-17): dedicated terminal screen for when the
  // LinkedIn account is younger than the required minimum (default six
  // months). The BE cancelled the session right after login (before any
  // email change), status=terminal_failed, terminal_reason=
  // 'account_too_young'. There is no recovery path — the seller simply has
  // an account that is too new. We surface the BE's seller-safe message
  // (status_message_safe_for_seller) and a clear, non-blaming explanation.
  return (
    <StepLayout title="This account is too new" variant="centered">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        padding: '32px 0',
      }}>
        <div style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(250, 107, 5, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
        }}>🕒</div>
        <p style={{
          color: 'var(--text)',
          fontSize: 16,
          fontWeight: 600,
          textAlign: 'center',
          margin: 0,
          maxWidth: 400,
        }}>
          {message
            || 'This LinkedIn account is too new to take over. We can only proceed '
               + 'with accounts that have been active on LinkedIn for at least six months.'}
        </p>
        <p style={{
          color: 'var(--text2)',
          fontSize: 13,
          lineHeight: 1.5,
          textAlign: 'center',
          margin: 0,
          maxWidth: 400,
        }}>
          There’s nothing wrong on your side, your account just hasn’t been
          around long enough yet. Please come back once it’s a little older.
          Questions? Reach us at{' '}
          <a href={`mailto:${supportEmail}`} style={{ color: 'var(--primary)' }}>
            {supportEmail}
          </a>
          .
        </p>
        <StartOverButton onStartOver={onStartOver} label="Back to start" />
      </div>
    </StepLayout>
  );
}

// FE error boundary — catches a runtime crash in a child screen and
// renders a graceful fallback without nuking the seller's session_id.
// Per directive: localStorage stays intact so a refresh resumes at the
// same session. The fallback surfaces the session_id so the seller can
// quote it to support.
export class TakeoverErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the crash to the console for ops; never expose stack
    // traces to the seller. Future improvement: ship to Sentry / GHL.
    // eslint-disable-next-line no-console
    console.error('[takeover-app] FE crash caught by ErrorBoundary:', error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    const sessionId = (typeof window !== 'undefined')
      ? (window.localStorage.getItem('takeover.sessionId') || null)
      : null;
    return (
      <StepLayout title="Something went wrong" variant="centered">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          padding: '32px 0',
        }}>
          <div style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'rgba(239, 68, 68, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 28,
            color: 'var(--danger, #ef4444)',
          }}>!</div>
          <p style={{
            color: 'var(--text)',
            fontSize: 16,
            fontWeight: 600,
            textAlign: 'center',
            margin: 0,
          }}>
            Something went wrong on our end.
          </p>
          <p style={{
            color: 'var(--text2)',
            fontSize: 13,
            lineHeight: 1.5,
            textAlign: 'center',
            margin: 0,
            maxWidth: 380,
          }}>
            Refresh this page to continue.{sessionId ? ' Your progress is saved.' : ''}
          </p>
          {sessionId && (
            <p style={{
              color: 'var(--text2)',
              fontSize: 11,
              fontFamily: 'monospace',
              textAlign: 'center',
              margin: 0,
            }}>
              Reference: {sessionId}
            </p>
          )}
          <button
            type="button"
            onClick={() => { if (typeof window !== 'undefined') window.location.reload(); }}
            style={{
              background: 'var(--primary)',
              color: '#fff',
              padding: '12px 24px',
              borderRadius: 8,
              border: 'none',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              marginTop: 8,
            }}
          >
            Refresh
          </button>
        </div>
      </StepLayout>
    );
  }
}
